import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import CalendarHeader from "./CalendarHeader"
import CalendarViewSwitcher from "./CalendarViewSwitcher"
import styles from "./index.module.css"

/**
 * Reusable Calendar Component - Data Driven Approach
 *
 * This is a presentational component that displays bookings in a calendar view.
 * It does NOT fetch data - the parent component should handle data fetching and pass filtered bookings.
 *
 * @example
 * // In crew member detail page:
 * const { bookings, loading } = useSelector(state => state.calendar)
 * const filteredBookings = bookings.filter(b => b.crew_member_ids?.includes(crewMemberId))
 *
 * <Calendar
 *   bookingsData={filteredBookings}
 *   loading={loading}
 *   initialDate={moment().format(DATE_FORMAT)}
 *   initialRange="week"
 *   showControls={{ addBooking: true, journal: false, scheduleTimeOff: false }}
 *   onDateChange={(date) => console.log('Date changed:', date)}
 *   onRangeChange={(range) => console.log('Range changed:', range)}
 *   onAddBooking={() => navigate('/bookings/new')}
 * />
 */
const Calendar = ({
  bookingsData = [],
  journalNoteDates = [],
  timeOffData = [],
  loading = false,
  date,
  range,
  showControls = {
    addBooking: true,
    journal: false,
    scheduleTimeOff: false
  },
  onChangeFilters,
  onAddBooking,
  onJournalClick,
  onScheduleTimeOffClick,
  canCreateJournal = true,
  canEditJournal = true,
  canViewJournal = true
}) => {
  // Component is fully controlled by parent - no internal state needed

  // Build params object from props
  const params = { date, range }

  return (
    <Flex vertical className={styles.container}>
      <div className={styles.header}>
        <CalendarHeader
          params={params}
          onChangeFilters={onChangeFilters}
          showControls={showControls}
          onAddBooking={onAddBooking}
          onJournalClick={onJournalClick}
          onScheduleTimeOffClick={onScheduleTimeOffClick}
          bookingsData={bookingsData}
        />
      </div>
      <div className={styles.calendarWrapper}>
        <div className={styles.calendar}>
          <CalendarViewSwitcher
            date={date}
            range={range}
            bookingsData={bookingsData}
            journalNoteDates={journalNoteDates}
            timeOffData={timeOffData}
            loading={loading}
            onJournalClick={onJournalClick}
            canCreateJournal={canCreateJournal}
            canEditJournal={canEditJournal}
            canViewJournal={canViewJournal}
          />
        </div>
      </div>
    </Flex>
  )
}

Calendar.propTypes = {
  // Data props (required)
  bookingsData: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      title: PropTypes.string,
      formatted_date: PropTypes.string,
      formatted_start_time: PropTypes.string,
      formatted_end_time: PropTypes.string,
      payment_status: PropTypes.string
    })
  ),
  journalNoteDates: PropTypes.array,
  timeOffData: PropTypes.array,
  loading: PropTypes.bool,

  // Controlled props - parent manages state
  date: PropTypes.string.isRequired,
  range: PropTypes.oneOf(["date", "week", "month"]).isRequired,

  // Control visibility
  showControls: PropTypes.shape({
    addBooking: PropTypes.bool,
    journal: PropTypes.bool,
    scheduleTimeOff: PropTypes.bool
  }),

  // Event handlers
  onChangeFilters: PropTypes.func.isRequired,
  onAddBooking: PropTypes.func,
  onJournalClick: PropTypes.func,
  onScheduleTimeOffClick: PropTypes.func,

  // Permissions
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  canViewJournal: PropTypes.bool
}

export default Calendar
